import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convex-api";

type SupportStatusFilter = "all" | "open" | "waiting_for_user" | "resolved";
type SupportSourceFilter = "all" | "customer" | "runner";
type SupportCategoryFilter =
	| "all"
	| "errand"
	| "payment"
	| "payout"
	| "account"
	| "kyc"
	| "safety"
	| "bug"
	| "private_task"
	| "other";

type SupportTicketRow = {
	_id: string;
	category: string;
	hasUnread: boolean;
	linkedSnapshot?: {
		subtitle?: string;
		title?: string;
	};
	requesterSnapshot: {
		email: string;
		name: string;
	};
	sourceApp: string;
	status: string;
	subject: string;
	updatedAt: number;
};

type SupportStats = {
	open: number;
	resolved: number;
	total: number;
	waitingForUser: number;
};

export const Route = createFileRoute("/_authenticated/support/")({
	component: SupportInboxPage,
});

function statusBadgeVariant(
	status: string,
): "default" | "destructive" | "outline" | "secondary" {
	switch (status) {
		case "resolved":
			return "default";
		case "waiting_for_user":
			return "secondary";
		default:
			return "outline";
	}
}

function formatRelative(timestamp: number): string {
	const diff = Date.now() - timestamp;
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.floor(hours / 24)}d ago`;
}

function StatStrip({ stats }: { stats: SupportStats | undefined }) {
	if (!stats) {
		return (
			<div className="grid overflow-hidden rounded-xl border sm:grid-cols-4">
				{Array.from({ length: 4 }).map((_, index) => (
					<div
						className="border-b px-4 py-4 sm:border-r sm:last:border-r-0 sm:border-b-0"
						key={index}
					>
						<Skeleton className="h-4 w-24" />
						<Skeleton className="mt-3 h-8 w-16" />
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="grid overflow-hidden rounded-xl border sm:grid-cols-4">
			{[
				{ label: "Open", value: stats.open },
				{ label: "Waiting", value: stats.waitingForUser },
				{ label: "Resolved", value: stats.resolved },
				{ label: "Total", value: stats.total },
			].map((item) => (
				<div
					className="border-b px-4 py-4 last:border-b-0 sm:border-r sm:last:border-r-0 sm:border-b-0"
					key={item.label}
				>
					<p className="text-muted-foreground text-xs uppercase tracking-[0.14em]">
						{item.label}
					</p>
					<p className="mt-2 font-semibold text-2xl">{item.value}</p>
				</div>
			))}
		</div>
	);
}

function SupportInboxPage() {
	const [statusFilter, setStatusFilter] = useState<SupportStatusFilter>("all");
	const [sourceFilter, setSourceFilter] = useState<SupportSourceFilter>("all");
	const [categoryFilter, setCategoryFilter] =
		useState<SupportCategoryFilter>("all");

	const stats = useQuery(api.support.getAdminSupportStats) as
		| SupportStats
		| undefined;
	const ticketData = useQuery(api.support.listAdminTickets, {
		status: statusFilter === "all" ? undefined : statusFilter,
		sourceApp: sourceFilter === "all" ? undefined : sourceFilter,
		category: categoryFilter === "all" ? undefined : categoryFilter,
		limit: 100,
	}) as { tickets: SupportTicketRow[] } | undefined;

	const tickets = ticketData?.tickets ?? [];

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-8">
				<h1 className="font-bold text-3xl">Support</h1>
				<p className="mt-1 text-muted-foreground text-sm">
					Review active support threads, dispute-linked issues, and customer
					follow-ups.
				</p>
			</div>

			<StatStrip stats={stats} />

			<div className="sticky top-0 z-10 mt-8 border-y bg-background/95 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
				<div className="grid gap-3 md:grid-cols-3">
					<Select
						onValueChange={(value) =>
							setStatusFilter(value as SupportStatusFilter)
						}
						value={statusFilter}
					>
						<SelectTrigger>
							<SelectValue placeholder="Status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All statuses</SelectItem>
							<SelectItem value="open">Open</SelectItem>
							<SelectItem value="waiting_for_user">Waiting for user</SelectItem>
							<SelectItem value="resolved">Resolved</SelectItem>
						</SelectContent>
					</Select>

					<Select
						onValueChange={(value) =>
							setSourceFilter(value as SupportSourceFilter)
						}
						value={sourceFilter}
					>
						<SelectTrigger>
							<SelectValue placeholder="App" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All apps</SelectItem>
							<SelectItem value="customer">Customer</SelectItem>
							<SelectItem value="runner">Runner</SelectItem>
						</SelectContent>
					</Select>

					<Select
						onValueChange={(value) =>
							setCategoryFilter(value as SupportCategoryFilter)
						}
						value={categoryFilter}
					>
						<SelectTrigger>
							<SelectValue placeholder="Category" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All categories</SelectItem>
							<SelectItem value="errand">Errand</SelectItem>
							<SelectItem value="payment">Payment</SelectItem>
							<SelectItem value="payout">Payout</SelectItem>
							<SelectItem value="account">Account</SelectItem>
							<SelectItem value="kyc">KYC</SelectItem>
							<SelectItem value="safety">Safety</SelectItem>
							<SelectItem value="bug">Bug</SelectItem>
							<SelectItem value="private_task">Private Task</SelectItem>
							<SelectItem value="other">Other</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className="mt-6 overflow-hidden rounded-xl border">
				{ticketData === undefined ? (
					<div className="space-y-3 px-4 py-4">
						{Array.from({ length: 7 }).map((_, index) => (
							<Skeleton className="h-12 w-full" key={index} />
						))}
					</div>
				) : tickets.length === 0 ? (
					<div className="px-4 py-12 text-center">
						<p className="font-medium text-sm">
							No support threads match these filters.
						</p>
						<p className="mt-1 text-muted-foreground text-sm">
							When customers or runners reach out, they’ll show up here.
						</p>
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Thread</TableHead>
								<TableHead>Requester</TableHead>
								<TableHead>App</TableHead>
								<TableHead>Category</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Updated</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{tickets.map((ticket) => (
								<TableRow key={ticket._id}>
									<TableCell>
										<div className="flex items-start gap-3">
											<div
												className="mt-1 size-2.5 rounded-full"
												style={{
													backgroundColor: ticket.hasUnread
														? "var(--primary)"
														: "var(--border)",
												}}
											/>
											<div className="min-w-0">
												<p className="truncate font-medium text-sm">
													{ticket.subject}
												</p>
												<p className="truncate text-muted-foreground text-xs">
													{ticket.linkedSnapshot?.title ??
														ticket.linkedSnapshot?.subtitle ??
														"No linked context"}
												</p>
											</div>
										</div>
									</TableCell>
									<TableCell>
										<div>
											<p className="text-sm">{ticket.requesterSnapshot.name}</p>
											<p className="text-muted-foreground text-xs">
												{ticket.requesterSnapshot.email}
											</p>
										</div>
									</TableCell>
									<TableCell>
										<Badge variant="outline">{ticket.sourceApp}</Badge>
									</TableCell>
									<TableCell>
										<Badge variant="secondary">
											{ticket.category.replaceAll("_", " ")}
										</Badge>
									</TableCell>
									<TableCell>
										<Badge variant={statusBadgeVariant(ticket.status)}>
											{ticket.status.replaceAll("_", " ")}
										</Badge>
									</TableCell>
									<TableCell className="text-muted-foreground text-sm">
										{formatRelative(ticket.updatedAt)}
									</TableCell>
									<TableCell>
										<Button asChild size="sm" variant="outline">
											<Link
												params={{ ticketId: ticket._id }}
												to="/support/$ticketId"
											>
												Open
											</Link>
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>
		</div>
	);
}
